import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Avatar from "../../../components/Avatar/Avatar";

// The backend returns one row per (peer, weak standard), already ranked
// strongest-first. Sorted by score alone the top rows skew toward whichever
// standard happens to have the strongest experts, so the rail ends up stacking
// four mentors for the same subdomain. Curate for breadth instead: round-robin
// across the distinct weak standards, taking each standard's next strongest
// not-yet-shown peer in turn, so the rail spans the teacher's different weak
// areas. Every teacher still appears at most once (the standard whose round
// claimed them). Peer scores stay server-side — we only reorder ranked rows.
const MAX_EXPERTS = 4;

// Remembered like the sidebar's own collapse, and for the same reason: a
// teacher who works from the standards grid wants the width back, and having to
// close the rail on every visit would be worse than the rail itself.
const STORAGE_KEY = "telos.peerRail.expanded";

const FOCUS_RING =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#141517]";

function PeopleIcon({ className = "w-6 h-6" }) {
  return (
    <svg aria-hidden="true" className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.5"
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

// The same panel glyph the sidebar toggle uses, so the two collapses read as
// one gesture rather than two unrelated controls.
function PanelIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2.5" strokeWidth="2" />
      <path d="M15 3v18" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export default function PeerExpertsRail({ experts, onRequestVisit, max = MAX_EXPERTS }) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(
    () => localStorage.getItem(STORAGE_KEY) !== "false",
  );
  const toggle = () =>
    setIsExpanded((v) => {
      localStorage.setItem(STORAGE_KEY, String(!v));
      return !v;
    });

  const topExperts = useMemo(() => {
    // Group by standard, preserving the backend's strongest-first order within
    // each group; groups themselves keep first-seen order (strongest standard
    // leads, since `experts` arrives globally score-sorted).
    const byStandard = new Map();
    for (const rec of experts) {
      const list = byStandard.get(rec.standard.uuid);
      if (list) list.push(rec);
      else byStandard.set(rec.standard.uuid, [rec]);
    }

    const groups = [...byStandard.values()];
    const seenTeachers = new Set();
    const picked = [];
    let advanced = true;
    while (picked.length < max && advanced) {
      advanced = false;
      for (const group of groups) {
        if (picked.length >= max) break;
        // This standard's next strongest peer we haven't shown yet.
        const rec = group.find((r) => !seenTeachers.has(r.teacher.uuid));
        if (!rec) continue;
        seenTeachers.add(rec.teacher.uuid);
        picked.push(rec);
        advanced = true;
      }
    }
    return picked;
  }, [experts, max]);

  if (topExperts.length === 0) return null;

  if (!isExpanded) {
    // A strip on desktop, a single bar on mobile. The main column is `flex-1`,
    // so it reclaims the width without being told to.
    return (
      <div className="w-full lg:w-14 shrink-0 lg:self-stretch">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={false}
          aria-label={t("teacher.peerExpertsExpand")}
          title={t("teacher.peerExpertsExpand")}
          className={`sticky top-8 w-full bg-[#141517] rounded-[20px] py-3 px-3 flex lg:flex-col items-center justify-center gap-3 text-white shadow-xl shadow-indigo-900/20 border border-white/5 hover:bg-[#1b1d20] transition-colors ${FOCUS_RING}`}
        >
          <span className="w-8 h-8 bg-indigo-500 rounded-xl flex items-center justify-center shrink-0">
            <PeopleIcon className="w-4 h-4" />
          </span>
          <span className="text-xs font-black uppercase tracking-widest lg:hidden">
            {t("teacher.peerExperts")}
          </span>
          <span className="w-5 h-5 rounded-md bg-white/10 text-[10px] font-black flex items-center justify-center shrink-0">
            {topExperts.length}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="lg:w-80 w-full shrink-0 lg:self-stretch">
      <div className="bg-[#141517] rounded-[32px] p-6 sticky top-8 shadow-2xl shadow-indigo-900/20 border border-white/5">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 bg-indigo-500 text-white rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-indigo-500/30">
            <PeopleIcon />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-black text-white uppercase tracking-[0.2em]">
              {t("teacher.peerExperts")}
            </h3>
            <p className="text-[10px] text-indigo-400 font-black uppercase tracking-widest mt-1">
              {t("teacher.recommended")}
            </p>
          </div>
          <button
            type="button"
            onClick={toggle}
            aria-expanded
            aria-label={t("teacher.peerExpertsCollapse")}
            title={t("teacher.peerExpertsCollapse")}
            className={`ml-auto shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors ${FOCUS_RING}`}
          >
            <PanelIcon />
          </button>
        </div>

        <div className="space-y-3">
          {topExperts.map((rec) => (
            <PeerExpertCard
              key={`${rec.teacher.uuid}-${rec.standard.uuid}`}
              rec={rec}
              onRequestVisit={onRequestVisit}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function PeerExpertCard({ rec, onRequestVisit }) {
  const { t } = useTranslation();
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 hover:bg-white/10 hover:border-white/20 transition-colors duration-500 group">
      <div className="flex items-center gap-4 mb-4">
        <div className="relative shrink-0">
          <Avatar
            user={rec.teacher}
            alt={rec.teacher.full_name}
            className="w-12 h-12 rounded-xl border-2 border-white/10 shadow-xl group-hover:scale-110 transition-transform duration-500 bg-indigo-600 text-white text-sm font-black"
          />
          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 text-white rounded-lg flex items-center justify-center border-2 border-[#141517] shadow-lg">
            <span className="text-[8px] font-black">S{rec.standard.number}</span>
          </div>
        </div>
        <div className="min-w-0">
          <h4 className="text-sm font-black text-white leading-tight text-pretty break-words group-hover:text-indigo-400 transition-colors">
            {rec.teacher.full_name}
          </h4>
          <p
            className="text-[11px] text-slate-400 font-medium leading-tight mt-1 break-words"
            title={rec.standard.title}
          >
            {t("teacher.expertIn", { title: rec.standard.title })}
          </p>
          {/* Peer's average score intentionally not shown — teachers must not
              see other teachers' performance figures. The backend no longer
              sends scores here; it returns the experts already ranked. */}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onRequestVisit?.(rec.teacher)}
        className="w-full py-2.5 bg-indigo-600 rounded-xl text-xs font-semibold text-white hover:bg-indigo-500 transition-colors duration-300 flex items-center justify-center gap-2 group/btn touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#141517]"
      >
        {t("teacher.requestVisit")}
        <svg
          aria-hidden="true"
          className="w-4 h-4 transition-transform group-hover/btn:translate-x-1.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="3"
            d="M14 5l7 7m0 0l-7 7m7-7H3"
          />
        </svg>
      </button>
    </div>
  );
}
